import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button, 
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Divider,
  Avatar,
  AvatarGroup,
  Skeleton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';  // Import Spanish locale
import 'moment/locale/ca';  // Import Catalan locale
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { event as eventApi, task as taskApi } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../components/auth/AuthModule';
import { motion, AnimatePresence } from 'framer-motion';
import { getTransformedImageUrl } from '../../utils/filestack';

// Icons
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LockIcon from '@mui/icons-material/Lock';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';

// Components
import EventDetailModal from '../events/components/EventDetailModal';
import CreateEventModal from '../events/components/CreateEventModal';

// Setup the localizer for the calendar
const localizer = momentLocalizer(moment);

const CalendarPage = () => {
  const { t, i18n } = useTranslation(['calendar', 'events']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [showOnlyEvents, setShowOnlyEvents] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEventDetailOpen, setIsEventDetailOpen] = useState(false);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Initialize moment with the current language on component mount
    moment.locale(i18n.language);
    return moment();
  });

  // Update moment's locale when language changes
  useEffect(() => {
    moment.locale(i18n.language);
    // Update currentMonth to trigger a re-render with new locale
    setCurrentMonth(prev => moment(prev));
  }, [i18n.language]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const eventsData = await eventApi.getAll();
      setEvents(eventsData);
      await fetchTasks();
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      toast.error(t('errors.fetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const tasksData = await taskApi.getMyTasks({ all: true });
      const tasksWithDueDates = (Array.isArray(tasksData) ? tasksData : tasksData.tasks || [])
        .filter(task => task.dueDate && task.dueDate !== null && task.dueDate !== '');
      setTasks(tasksWithDueDates);
      return tasksWithDueDates;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error(t('errors.fetchingTasks'));
      return [];
    }
  };

  const handleEventSelect = (item) => {
    if (item.type === 'event') {
      navigate(`/events/${item.resource._id}`);
    } else if (item.type === 'task') {
      navigate(`/workshop/tasks/${item.resource._id}`);
    }
  };

  const handleViewTaskDetail = () => {
    if (selectedTask) {
      navigate(`/workshop/tasks/${selectedTask._id}`);
    }
    setIsTaskDetailOpen(false);
  };

  const handleCreateEventSuccess = () => {
    setIsCreateEventOpen(false);
    fetchData();
    toast.success(t('success.eventCreated', { ns: 'events' }));
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(moment(currentMonth).subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(moment(currentMonth).add(1, 'month'));
  };

  const getCurrentMonthItems = () => {
    const startOfMonth = moment(currentMonth).startOf('month');
    const endOfMonth = moment(currentMonth).endOf('month');

    const filteredEvents = events
      .filter(event => {
        const eventDate = moment(event.date);
        const isInMonth = eventDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
        
        if (!isInMonth) return false;
        
        if (showOnlyMine) {
          return (
            event.createdBy._id === user._id || // I created it
            event.participants?.some(p => p.user._id === user._id) || // I'm a participant
            (event.invitedUsers && event.invitedUsers.some(u => // I'm invited
              (u._id ? u._id === user._id : u === user._id)))
          );
        }
        
        return true;
      })
      .map(event => ({
        ...event,
        type: 'event',
        date: moment(event.date),
        resource: event
      }));

    const filteredTasks = !showOnlyEvents ? tasks
      .filter(task => {
        const taskDate = moment(task.dueDate);
        const isInMonth = taskDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
        
        if (!isInMonth) return false;
        
        if (showOnlyMine) {
          return (
            task.assignedTo?._id === user._id || // Task assigned to me
            task.createdBy?._id === user._id || // I created the task
            task.car?.owner?._id === user._id // Task is for my car
          );
        }
        
        return true;
      })
      .map(task => ({
        ...task,
        type: 'task',
        date: moment(task.dueDate),
        resource: task
      })) : [];

    return [...filteredEvents, ...filteredTasks]
      .sort((a, b) => a.date.valueOf() - b.date.valueOf());
  };

  const renderMonthHeader = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 2,
        mb: 3
      }}
    >
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}>
            {currentMonth.format('MMMM YYYY').charAt(0).toUpperCase() + currentMonth.format('MMMM YYYY').slice(1)}
          </Typography>
          <Box>
            <IconButton onClick={handlePreviousMonth} color="primary" size="small">
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={handleNextMonth} color="primary" size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

      </Box>

      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' }
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyEvents}
              onChange={(e) => setShowOnlyEvents(e.target.checked)}
              size="small"
            />
          }
          label={t('calendar.showOnlyEvents')}
          sx={{ 
            m: 0,
            flex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyMine}
              onChange={(e) => setShowOnlyMine(e.target.checked)}
              size="small"
            />
          }
          label={t('calendar.showOnlyMine')}
          sx={{ 
            m: 0,
            flex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1
          }}
        />
      </Box>
    </Box>
  );

  const renderItemCard = (item) => {
    const isEvent = item.type === 'event';
    const itemData = item.resource;

    const getImage = () => {
      if (isEvent) {
        return itemData.imageUrl ? getTransformedImageUrl(itemData.imageUrl, 'card') : null;
      } else {
        if (itemData.car?.imageUrl) {
          return getTransformedImageUrl(itemData.car.imageUrl, 'card');
        } else if (itemData.car?.images?.length > 0) {
          return getTransformedImageUrl(itemData.car.images[0], 'card');
        }
        return null;
      }
    };

    const image = getImage();

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        key={`${item.type}_${itemData._id}`}
      >
        <Card 
          sx={{ 
            mb: 2,
            cursor: 'pointer',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 6,
              transition: 'all 0.3s ease'
            }
          }}
          onClick={() => handleEventSelect(item)}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
            {image ? (
              <Box 
                sx={{ 
                  width: { xs: '100%', sm: 200 },
                  height: { xs: 200, sm: 'auto' },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={itemData.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: isEvent ? 'primary.main' : 'error.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {isEvent ? <EventIcon /> : <AssignmentIcon />}
                </Box>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  width: { xs: '100%', sm: 200 },
                  height: { xs: 200, sm: 'auto' },
                  bgcolor: isEvent ? 'primary.dark' : 'error.dark',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isEvent ? (
                  <EventIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)' }} />
                ) : (
                  <AssignmentIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)' }} />
                )}
              </Box>
            )}

            <CardContent sx={{ flex: 1, p: 3 }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box>
                  <Typography variant="h6" component="div" gutterBottom>
                    {itemData.title}
                  </Typography>
                  {!isEvent && itemData.car && (
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {itemData.car.brand} {itemData.car.model}
                    </Typography>
                  )}
                </Box>
                {isEvent && itemData.isPrivate && (
                  <LockIcon fontSize="small" color="action" />
                )}
              </Box>

              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {item.date.format('DD MMM, HH:mm')}
                  </Typography>
                </Box>
                
                {isEvent && itemData.location && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {itemData.location}
                    </Typography>
                  </Box>
                )}

                {isEvent && itemData.participants && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <GroupIcon fontSize="small" color="action" />
                    <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                      {itemData.participants.map((participant) => (
                        <Avatar 
                          key={participant.user._id}
                          alt={participant.user.name}
                          src={participant.user.imageUrl ? getTransformedImageUrl(participant.user.imageUrl, 'avatar') : undefined}
                        >
                          {participant.user.name?.[0]}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                  </Box>
                )}

                {!isEvent && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      size="small"
                      label={itemData.completed ? t('calendar.completed') : t('calendar.pending')}
                      color={itemData.completed ? "success" : "warning"}
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Box>
        </Card>
      </motion.div>
    );
  };

  const renderSkeletonCard = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        sx={{ 
          mb: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box 
            sx={{ 
              width: { xs: '100%', sm: 200 },
              height: { xs: 200, sm: 'auto' },
              position: 'relative'
            }}
          >
            <Skeleton 
              variant="rectangular" 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'background.paper'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                width: 32,
                height: 32
              }}
            >
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </Box>

          <CardContent sx={{ flex: 1, p: 3 }}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Box width="100%">
                <Skeleton variant="text" width="80%" height={32} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="50%" height={24} />
              </Box>
            </Box>

            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
              
              <Box display="flex" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="60%" height={24} />
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Box display="flex" gap={1}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="circular" width={24} height={24} />
                  ))}
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Box>
      </Card>
    </motion.div>
  );

  const renderSkeletonHeader = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 2,
        mb: 3
      }}
    >
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Skeleton variant="text" width={300} height={48} />
          <Box>
            <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block', mr: 1 }} />
            <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block' }} />
          </Box>
        </Box>
      </Box>

      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' }
        }}
      >
        <Skeleton variant="rounded" width="100%" height={40} />
        <Skeleton variant="rounded" width="100%" height={40} />
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {loading ? (
          <>
            {renderSkeletonHeader()}
            <AnimatePresence mode="wait">
              {[1, 2, 3, 4].map((item) => (
                <React.Fragment key={item}>
                  {renderSkeletonCard()}
                </React.Fragment>
              ))}
            </AnimatePresence>
          </>
        ) : (
          <>
            {renderMonthHeader()}
            
            <AnimatePresence mode="wait">
              {getCurrentMonthItems().map(item => renderItemCard(item))}
            </AnimatePresence>

            {getCurrentMonthItems().length === 0 && (
              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  bgcolor: 'background.default'
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('calendar.noItemsThisMonth')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateEventOpen(true)}
                  sx={{ mt: 2 }}
                >
                  {t('calendar.addEvent')}
                </Button>
              </Paper>
            )}
          </>
        )}
      </Box>

      <EventDetailModal
        open={isEventDetailOpen}
        onClose={() => setIsEventDetailOpen(false)}
        eventId={selectedEvent?._id}
        onEventUpdated={fetchData}
      />

      <CreateEventModal
        open={isCreateEventOpen}
        onClose={() => setIsCreateEventOpen(false)}
        onSuccess={handleCreateEventSuccess}
      />

      <Dialog
        open={isTaskDetailOpen}
        onClose={() => setIsTaskDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedTask?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {selectedTask?.description}
          </Typography>
          
          <Box mt={2}>
            <Typography variant="subtitle2">
              {t('calendar.assignedTo')}:
            </Typography>
            <Typography variant="body2">
              {selectedTask?.assignedTo?.name || t('calendar.unassigned')}
            </Typography>
          </Box>
          
          <Box mt={2}>
            <Typography variant="subtitle2">
              {t('calendar.dueDate')}:
            </Typography>
            <Typography variant="body2">
              {selectedTask?.dueDate 
                ? moment(selectedTask.dueDate).format('LL') 
                : t('calendar.noDueDate')}
            </Typography>
          </Box>
          
          <Box mt={2}>
            <Typography variant="subtitle2">
              {t('calendar.status')}:
            </Typography>
            <Chip 
              label={selectedTask?.completed ? t('calendar.completed') : t('calendar.pending')}
              color={selectedTask?.completed ? "success" : "warning"}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTaskDetailOpen(false)}>
            {t('common.close', { ns: 'events' })}
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleViewTaskDetail}
          >
            {t('calendar.viewDetails')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CalendarPage; 